import { useClientWebRouteProps } from '@cp/entities/clientWebRoutes';
import { useRouter } from '@cp/shared/router';
import { difference } from 'lodash';
import { useCallback } from 'react';
import { useInjection } from '@cp/shared/di';
import { useCurrentCity } from '@cp/entities/location';
import pickBy from 'lodash/pickBy';
import { useClientCity } from 'src/app/shared/hooks/useClientCity';
import { ClientSelection, clientProfileToSelection, hasSelections, useClientProfile } from 'src/entities/client';
import { CATALOG_QUIZ } from '../di';
import { RequiredSelections, useBuildCatalogUrl } from './useBuildCatalogUrl';

type RequiredSelectionKeys = Array<keyof RequiredSelections>;
const requiredSelectionKeys: RequiredSelectionKeys = ['cityId', 'careerId', 'catalogType'];
const isRequiredSelection = (cs: ClientSelection): cs is RequiredSelections => hasSelections(cs, requiredSelectionKeys);

/**
 * Направляет клиента в релевантный каталог
 */
export const useNavigateToCatalog = (props: ClientSelection = {}) => {
  const clientProfile = useClientProfile();
  const pageProps = useClientWebRouteProps();
  const router = useRouter();
  const { isLoading } = useClientCity();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const buildCatalogUrl = useBuildCatalogUrl();
  const quiz = useInjection(CATALOG_QUIZ);

  const navigateToCatalog = useCallback(
    async (opts: ClientSelection = {}): Promise<void> => {
      let mergedSelection: ClientSelection = pickBy(
        Object.assign(
          { cityId: currentCity?.id } as ClientSelection,
          pageProps,
          clientProfile ? clientProfileToSelection(clientProfile) : {},
          { careerId: undefined }, // do not auto detect careerId. Ask if not explicit provided
          props,
          opts,
        ),
        Boolean,
      );
      while (!isRequiredSelection(mergedSelection)) {
        const missedKeys = difference(requiredSelectionKeys, Object.keys(mergedSelection));
        const answers = await quiz(missedKeys, mergedSelection);
        if (answers.isErr) {
          // exit on quiz cancel with suppressing error
          return;
        }
        mergedSelection = Object.assign(answers.unwrap(), mergedSelection);
      }
      const urlResult = await buildCatalogUrl(mergedSelection);
      await urlResult.mapAsync((url) => router.navigate(url));
    },
    [router, props, pageProps, buildCatalogUrl, clientProfile, quiz, currentCity],
  );

  return {
    isLoading,
    navigateToCatalog,
  };
};
